import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  analyticsApi,
  type AnalyticsPeriod,
  type PctileBlock,
} from "@/features/analytics/api";

const PERIODS: { value: AnalyticsPeriod; label: string }[] = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const PIPELINES = [
  { value: "all", label: "All pipelines" },
  { value: "v4", label: "v4 (Flux)" },
  { value: "v3", label: "v3" },
  { value: "twilio", label: "Twilio" },
];

function fmtMs(ms: number | null | undefined) {
  if (!ms || ms <= 0) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)} ms`;
}

export default function LatencyPage() {
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const [pipeline, setPipeline] = useState("all");
  const [channel, setChannel] = useState<"all" | "whatsapp" | "phone">("all");
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ["latency-console", period, pipeline, channel],
    queryFn: () =>
      analyticsApi.latencyConsole({
        period,
        pipeline_version: pipeline === "all" ? undefined : pipeline,
        channel: channel === "all" ? undefined : channel,
      }),
  });

  const data = q.data;
  const hasData = (data?.summary.sample_count ?? 0) > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Latency</h1>
          <p className="text-sm text-muted-foreground">
            Per-stage voice latency: where each turn's time goes, end to end.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={channel}
            onValueChange={(v) => setChannel(v as "all" | "whatsapp" | "phone")}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          <Select value={pipeline} onValueChange={setPipeline}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PIPELINES.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(period)}
            onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={String(p.value)}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : !hasData ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            No measured turns in this window.
            {pipeline === "v4" && (
              <>
                {" "}
                v4 traces appear once a call runs through the Flux pipeline.
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* E2E percentile cards */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <PctCard label="E2E p50" value={data!.summary.ttfa.p50} />
            <PctCard label="E2E p90" value={data!.summary.ttfa.p90} />
            <PctCard label="E2E p95" value={data!.summary.ttfa.p95} />
            <PctCard label="E2E p99" value={data!.summary.ttfa.p99} />
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            E2E = caller stops speaking → bot's first audio.{" "}
            {data!.summary.sample_count.toLocaleString()} turns · avg{" "}
            {fmtMs(data!.summary.ttfa.avg)}
          </p>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Stage waterfall */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Average time per stage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data!.waterfall}
                    layout="vertical"
                    margin={{ left: 24, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `${v}`} />
                    <YAxis
                      type="category"
                      dataKey="stage"
                      width={90}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip formatter={(value) => fmtMs(Number(value))} />
                    <Bar
                      dataKey="avg_ms"
                      fill="hsl(var(--primary))"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* E2E distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">E2E distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data!.distribution}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar
                      dataKey="count"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Per-stage percentile table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-stage breakdown</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Stage</th>
                    <th className="py-2 pr-4 font-medium">Avg</th>
                    <th className="py-2 pr-4 font-medium">p50</th>
                    <th className="py-2 pr-4 font-medium">p90</th>
                    <th className="py-2 pr-4 font-medium">p95</th>
                    <th className="py-2 pr-4 font-medium">p99</th>
                  </tr>
                </thead>
                <tbody>
                  <StageRow label="Endpointing" b={data!.summary.stages.endpointing} />
                  <StageRow label="STT" b={data!.summary.stages.stt} />
                  <StageRow label="LLM (TTFT)" b={data!.summary.stages.llm_ttft} />
                  <StageRow label="TTS (TTFB)" b={data!.summary.stages.tts_ttfb} />
                  <StageRow label="Tools" b={data!.summary.stages.tool} />
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Provider comparison */}
          {data!.providers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Providers</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">STT</th>
                      <th className="py-2 pr-4 font-medium">LLM</th>
                      <th className="py-2 pr-4 font-medium">TTS</th>
                      <th className="py-2 pr-4 font-medium">Turns</th>
                      <th className="py-2 pr-4 font-medium">Avg E2E</th>
                      <th className="py-2 pr-4 font-medium">p50</th>
                      <th className="py-2 pr-4 font-medium">p95</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.providers.map((p, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-4">{p.stt || "—"}</td>
                        <td className="py-2 pr-4">
                          {p.llm || "—"}
                          {p.llm_model ? (
                            <span className="text-muted-foreground">
                              {" "}
                              {p.llm_model}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2 pr-4">{p.tts || "—"}</td>
                        <td className="py-2 pr-4">{p.samples}</td>
                        <td className="py-2 pr-4">{fmtMs(p.avg_ttfa_ms)}</td>
                        <td className="py-2 pr-4">{fmtMs(p.p50_ttfa_ms)}</td>
                        <td className="py-2 pr-4">{fmtMs(p.p95_ttfa_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Worst turns */}
          {data!.worst_calls.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Slowest turns</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Agent</th>
                      <th className="py-2 pr-4 font-medium">When</th>
                      <th className="py-2 pr-4 font-medium">Turn</th>
                      <th className="py-2 pr-4 font-medium">E2E</th>
                      <th className="py-2 pr-4 font-medium">Endpoint</th>
                      <th className="py-2 pr-4 font-medium">STT</th>
                      <th className="py-2 pr-4 font-medium">LLM</th>
                      <th className="py-2 pr-4 font-medium">TTS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.worst_calls.map((w, i) => (
                      <tr
                        key={i}
                        onClick={() => navigate(`/calls/${w.call_id}`)}
                        className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      >
                        <td className="py-2 pr-4">{w.agent_name || "—"}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {w.started_at
                            ? new Date(w.started_at).toLocaleString()
                            : "—"}
                        </td>
                        <td className="py-2 pr-4">#{w.turn_index}</td>
                        <td className="py-2 pr-4 font-medium">
                          {fmtMs(w.ttfa_ms)}
                        </td>
                        <td className="py-2 pr-4">{fmtMs(w.endpointing_ms)}</td>
                        <td className="py-2 pr-4">{fmtMs(w.stt_ms)}</td>
                        <td className="py-2 pr-4">{fmtMs(w.llm_ttft_ms)}</td>
                        <td className="py-2 pr-4">{fmtMs(w.tts_ttfb_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function PctCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-1 text-2xl font-semibold">{fmtMs(value)}</div>
      </CardContent>
    </Card>
  );
}

function StageRow({ label, b }: { label: string; b: PctileBlock }) {
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4 font-medium">{label}</td>
      <td className="py-2 pr-4">{fmtMs(b.avg)}</td>
      <td className="py-2 pr-4">{fmtMs(b.p50)}</td>
      <td className="py-2 pr-4">{fmtMs(b.p90)}</td>
      <td className="py-2 pr-4">{fmtMs(b.p95)}</td>
      <td className="py-2 pr-4">{fmtMs(b.p99)}</td>
    </tr>
  );
}
